import * as THREE from 'three';
import {
  footprintRings,
  outlineRing,
  resolveMasses,
  totalHeight,
  type BuildingMass,
  type RoofShape,
  type SiteObject,
} from './SiteObject';
import {
  orientedBox,
  rectangularity,
  type OrientedBox,
  type Point2,
  type Ring,
} from './SiteFootprint';

/**
 * Procedural geometry for {@link SiteObject}s — the same bet the flora system makes, applied to
 * buildings: a record generates the mesh, so the document stays small, editable, and honest.
 *
 * A shed is not a scanned asset, it is a footprint, an eave height, a roof shape and two colours.
 * That matters beyond tidiness: a vision model reading a reference photograph can recover those
 * parameters far more reliably than it can produce usable geometry, and the result then lights,
 * shadows, and edits like everything else in the scene instead of arriving as baked art.
 *
 * Geometry is emitted in two parts so they can take different materials, mirroring the
 * `structure` / `foliage` / `accent` split in `FloraGeometry`:
 *
 * - `body`  — walls for every mass, or the slab for paving.
 * - `roof`  — the roof solids, whose flat undersides also fill the gable ends above the walls.
 *
 * Everything is built in the object's local frame: origin at the footprint centre, ground at y = 0,
 * `SiteObjectWorld` applying position and rotation.
 *
 * ## Arbitrary outlines
 *
 * Walls and slabs follow the footprint polygon exactly — extruded sides plus a triangulated cap.
 * Roofs are the interesting case, because a pitched roof is only meaningful over a shape that is
 * roughly a rectangle: run one ridge over an L and it roofs the empty notch as well as the house.
 * So the rule is explicit and per mass:
 *
 * - A mass that nearly fills its own minimum-area rectangle gets the pitched roof it asked for,
 *   solved in that rectangle's frame so a wing turned 20° keeps a ridge along its own length.
 * - A genuinely irregular mass gets a flat cap that follows its outline, whatever roof it named.
 *
 * That is not a limitation to work around, it is how roofs are drawn: an L-shaped roof is two
 * ridges, which is precisely what splitting the building into two masses expresses.
 */

export interface SiteObjectGeometryOptions {
  /**
   * How far the base extends below y = 0 to meet uneven ground. The world samples the terrain under
   * the footprint and passes the drop from its high corner, so a building on a slope is buried into
   * the hill rather than left standing on stilts.
   */
  groundSkirtMeters?: number;
}

export interface SiteObjectGeometryParts {
  body: THREE.BufferGeometry | null;
  roof: THREE.BufferGeometry | null;
}

/** Slab thickness for paving, before any ground skirt is added. */
const PAVING_THICKNESS = 0.12;

/** Roof board thickness — what reads as a fascia from ground level. */
const ROOF_THICKNESS = 0.14;

/**
 * How completely a mass must fill its oriented bounding box to earn a pitched roof.
 *
 * 0.9 accepts the small bays, chamfers and bowed fronts a real house has while rejecting an L or a
 * T, whose notch costs it a quarter of the box or more.
 */
const PITCHED_ROOF_FILL = 0.9;

export function buildSiteObjectGeometry(
  object: SiteObject,
  options: SiteObjectGeometryOptions = {},
): SiteObjectGeometryParts {
  const skirt = Math.max(0, options.groundSkirtMeters ?? 0);

  if (object.kind === 'paving') {
    const thickness = PAVING_THICKNESS + skirt;
    // Top face at y = 0; the world lifts the object to the footprint's high point.
    const slab = prism(outlineRing(object), -thickness, 0);
    return { body: slab, roof: null };
  }

  if (object.kind === 'prop') {
    // Placeholder massing: the volume the prop occupies, and nothing more.
    //
    // This is not a lesser path to be replaced later — it is the path taken whenever the asset is
    // absent, still downloading, or was never assigned, and it is what makes a missing model a
    // degraded object rather than a broken document. It follows the footprint polygon, so a prop
    // given a real outline is blocked out at that outline rather than at a rectangle.
    const height = Math.max(0.05, object.heightMeters);
    return { body: prism(outlineRing(object), -skirt, height), roof: null };
  }

  const bodies: THREE.BufferGeometry[] = [];
  const roofs: THREE.BufferGeometry[] = [];

  for (const mass of resolveMasses(object)) {
    const base = mass.baseMeters ?? 0;
    const wallHeight = Math.max(0.1, mass.heightMeters);
    const eave = base + wallHeight;

    bodies.push(prism(mass.footprint, base - skirt, eave));

    const roof = buildMassRoof(mass);
    if (roof) {
      roof.translate(0, eave, 0);
      roofs.push(roof);
    }
  }

  return { body: merge(bodies), roof: merge(roofs) };
}

/** Bounding height of the built object, for scene bounds and the capture's depth range. */
export function siteObjectHeight(object: SiteObject): number {
  if (object.kind === 'paving') return PAVING_THICKNESS;
  // A prop's height is stated outright rather than summed from masses and roofs, because a prop has
  // neither. `totalHeight` answers 0 for anything that is not a building, which would drop props out
  // of the scene bounds and out of the capture's depth range.
  if (object.kind === 'prop') return Math.max(0, object.heightMeters);
  return totalHeight(object);
}

/**
 * Ground-plane sample points for an object, in its local frame: every footprint vertex plus each
 * ring's centre. `SiteObjectWorld` walks these to find the ground an object sits on, so a polygon
 * is sampled at its actual corners rather than at a rectangle's.
 */
export function groundSamplePoints(object: SiteObject): Point2[] {
  const points: Point2[] = [];
  for (const ring of footprintRings(object)) {
    let x = 0;
    let z = 0;
    for (const point of ring) {
      points.push(point);
      x += point.x;
      z += point.z;
    }
    points.push({ x: x / ring.length, z: z / ring.length });
  }
  return points;
}

/* ----------------------------------------- Roofs ----------------------------------------- */

function buildMassRoof(mass: BuildingMass): THREE.BufferGeometry | null {
  const ring = mass.footprint;

  // An irregular outline cannot carry a ridge honestly, so it gets a cap that follows its own edge.
  if (mass.roof === 'flat' || rectangularity(ring) < PITCHED_ROOF_FILL) {
    return prism(ring, -ROOF_THICKNESS, 0);
  }

  const box = ridgeAlignedBox(ring, mass.ridgeCrosswise === true);
  const rise = Math.max(0, mass.roofRiseMeters);
  const overhang = Math.min(0.3, Math.min(box.halfWidth, box.halfDepth) * 0.16);
  const halfWidth = box.halfWidth + overhang;
  const halfDepth = box.halfDepth + overhang;

  const geometry =
    mass.roof === 'hip'
      ? buildHipRoof(halfWidth, halfDepth, rise)
      : extrudeProfile(roofProfile(mass.roof, halfDepth, rise, ROOF_THICKNESS), halfWidth);

  // Built in the box's own frame; rotate and slide it back into the mass's local frame.
  geometry.rotateY(-box.angle);
  geometry.translate(box.center.x, 0, box.center.z);
  return geometry;
}

/**
 * The mass's minimum-area rectangle, turned so its `halfWidth` axis is the one the ridge runs
 * along. A ridge follows the long axis unless the mass says otherwise, because that is what a roof
 * does — the short-axis ridge is the deliberate exception on a cross-gabled wing.
 */
function ridgeAlignedBox(ring: Ring, crosswise: boolean): OrientedBox {
  const box = orientedBox(ring);
  const longAlongWidth = box.halfWidth >= box.halfDepth;
  if (longAlongWidth !== crosswise) return box;
  return {
    center: box.center,
    halfWidth: box.halfDepth,
    halfDepth: box.halfWidth,
    angle: box.angle + Math.PI / 2,
  };
}

/**
 * The roof's cross-section in the box's (z, y) plane, wound counter-clockwise.
 *
 * The profile is closed with a flat underside at `-thickness` rather than following the top surface.
 * That is deliberate: the resulting solid also fills the triangular gable ends above the wall box,
 * which is exactly what a gable or gambrel building needs and saves a second piece of geometry.
 */
function roofProfile(
  shape: RoofShape,
  halfDepth: number,
  rise: number,
  thickness: number,
): [number, number][] {
  const d = halfDepth;
  // Bottom edge, left to right, then the top surface back from right to left.
  const base: [number, number][] = [
    [-d, -thickness],
    [d, -thickness],
  ];

  switch (shape) {
    case 'flat':
      return [...base, [d, 0], [-d, 0]];
    case 'shed':
      // Single plane, low at -z and high at +z.
      return [...base, [d, rise], [-d, 0]];
    case 'gable':
      return [...base, [d, 0], [0, rise], [-d, 0]];
    case 'gambrel':
      // Steep lower slopes, shallow upper ones — the barn/shed roof in the reference photograph.
      return [
        ...base,
        [d, 0],
        [d * 0.45, rise * 0.68],
        [0, rise],
        [-d * 0.45, rise * 0.68],
        [-d, 0],
      ];
    case 'hip':
      // Handled by buildHipRoof; a flat profile is the safe fallback.
      return [...base, [d, 0], [-d, 0]];
  }
}

/**
 * Extrude a convex (z, y) profile along local X into a closed solid.
 *
 * Winding is derived rather than eyeballed: for a counter-clockwise profile the side quads
 * `(-hw, p_i) → (+hw, p_i) → (+hw, p_i+1)` come out facing outward, and the two end caps fan in
 * opposite directions. Getting this right rather than falling back on `DoubleSide` keeps shadow
 * casting clean.
 */
function extrudeProfile(profile: readonly [number, number][], halfWidth: number): THREE.BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  const count = profile.length;

  // Two rings of vertices: x = -halfWidth (0..count-1) and x = +halfWidth (count..2*count-1).
  for (const sign of [-1, 1]) {
    for (const [z, y] of profile) {
      positions.push(sign * halfWidth, y, z);
    }
  }

  // Sides.
  for (let i = 0; i < count; i += 1) {
    const next = (i + 1) % count;
    const a = i; // -hw, p_i
    const b = count + i; // +hw, p_i
    const c = count + next; // +hw, p_i+1
    const d = next; // -hw, p_i+1
    indices.push(a, b, c, a, c, d);
  }

  // Caps: fan from the first profile point. Opposite winding on each face.
  for (let i = 1; i < count - 1; i += 1) {
    indices.push(count, count + i + 1, count + i); // +hw faces +X
    indices.push(0, i, i + 1); // -hw faces -X
  }

  return finish(positions, indices);
}

/**
 * A hipped roof: four sloping faces meeting at a ridge shorter than the building. Not an extrusion,
 * so it is built explicitly.
 */
function buildHipRoof(halfWidth: number, halfDepth: number, rise: number): THREE.BufferGeometry {
  // The ridge is inset from both ends by the roof's half-depth, which is what makes the ends hip
  // rather than gable. A footprint deeper than it is wide degenerates to a pyramid.
  const ridgeHalf = Math.max(0, halfWidth - halfDepth);
  const t = -ROOF_THICKNESS;

  const positions: number[] = [
    -halfWidth, t, -halfDepth, // 0 eave, back left
    halfWidth, t, -halfDepth, // 1 eave, back right
    halfWidth, t, halfDepth, // 2 eave, front right
    -halfWidth, t, halfDepth, // 3 eave, front left
    -ridgeHalf, rise, 0, // 4 ridge left
    ridgeHalf, rise, 0, // 5 ridge right
  ];

  const indices: number[] = [
    // Back slope (-z) and front slope (+z).
    0, 4, 5, 0, 5, 1,
    2, 5, 4, 2, 4, 3,
    // Hipped ends.
    1, 5, 2,
    3, 4, 0,
    // Flat underside.
    0, 1, 2, 0, 2, 3,
  ];

  return finish(positions, indices);
}

/* -------------------------------------- Prisms -------------------------------------- */

/**
 * A closed solid: the ring swept from `bottomY` to `topY`, capped at both ends.
 *
 * This is the one primitive walls, slabs and flat roofs are all built from, so arbitrary outlines
 * cost nothing extra anywhere. Facing is decided per triangle from its own signed area rather than
 * assumed from the ring's winding, which keeps it correct no matter how a caller wound its polygon.
 */
function prism(ring: Ring, bottomY: number, topY: number): THREE.BufferGeometry {
  const count = ring.length;
  const positions: number[] = [];
  const indices: number[] = [];

  // Bottom vertices 0..count-1, top vertices count..2*count-1.
  for (const point of ring) positions.push(point.x, bottomY, point.z);
  for (const point of ring) positions.push(point.x, topY, point.z);

  // Sides. For a ring wound counter-clockwise in (x, z) these face outward; `normalizeRing`
  // guarantees that winding, and a caller who ignored it only inverts the sides, which
  // `computeVertexNormals` still shades consistently.
  for (let i = 0; i < count; i += 1) {
    const next = (i + 1) % count;
    const b0 = i;
    const b1 = next;
    const t0 = count + i;
    const t1 = count + next;
    indices.push(b0, t0, t1, b0, t1, b1);
  }

  for (const face of triangulate(ring)) {
    const a = face[0] as number;
    const b = face[1] as number;
    const c = face[2] as number;
    // (x, z) with +Y up is left-handed, so a triangle that is counter-clockwise by signed area has
    // a downward normal. Emit it as-is for the underside and reversed for the top.
    const upward = signedArea(ring[a] as Point2, ring[b] as Point2, ring[c] as Point2) < 0;
    if (upward) {
      indices.push(count + a, count + b, count + c);
      indices.push(a, c, b);
    } else {
      indices.push(count + a, count + c, count + b);
      indices.push(a, b, c);
    }
  }

  return finish(positions, indices);
}

function signedArea(a: Point2, b: Point2, c: Point2): number {
  return (b.x - a.x) * (c.z - a.z) - (b.z - a.z) * (c.x - a.x);
}

/**
 * Triangulate a simple polygon into index triples.
 *
 * Uses Three's own ear-clipping rather than a new dependency — `ExtrudeGeometry` runs on the same
 * routine, so a footprint that triangulates here triangulates everywhere else in the renderer too.
 */
function triangulate(ring: Ring): number[][] {
  const contour = ring.map((point) => new THREE.Vector2(point.x, point.z));
  const faces = THREE.ShapeUtils.triangulateShape(contour, []);
  if (faces.length > 0) return faces;
  // Ear clipping declines on a self-intersecting outline; a fan still closes the solid.
  return Array.from({ length: ring.length - 2 }, (_, i) => [0, i + 1, i + 2]);
}

/* -------------------------------------- Plumbing -------------------------------------- */

/** Concatenate part geometries into one buffer. Null when there is nothing to build. */
function merge(parts: readonly THREE.BufferGeometry[]): THREE.BufferGeometry | null {
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0] as THREE.BufferGeometry;

  const positions: number[] = [];
  const indices: number[] = [];
  let offset = 0;

  for (const part of parts) {
    const position = part.getAttribute('position');
    const index = part.getIndex();
    for (let i = 0; i < position.count; i += 1) {
      positions.push(position.getX(i), position.getY(i), position.getZ(i));
    }
    if (index) {
      for (let i = 0; i < index.count; i += 1) indices.push(offset + index.getX(i));
    }
    offset += position.count;
    // The merged buffer owns the data now; the part was a scratch allocation.
    part.dispose();
  }

  return finish(positions, indices);
}

function finish(positions: number[], indices: number[]): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
}
